import { isIOS } from "react-device-detect";

export const requestNotificationPermission = (
  callback: () => void,
  setNotificationPermission: (
    value: React.SetStateAction<NotificationPermission | undefined>
  ) => void
) => {
  if (!("Notification" in window)) {
    alert("This browser does not support notifications.");
    callback();
  } else if (Notification.permission === "granted") {
    alert("You have already granted permission for notifications.");
    callback();
  } else if (Notification.permission !== "denied") {
    Notification.requestPermission().then((permission) => {
      setNotificationPermission(permission);
      callback();
    });
  } else {
    alert(
      "You have denied permission for notifications. Please enable it in your browser settings."
    );
    callback();
  }
};

export const sendNotification = (title: string, options: any) => {
  if (Notification.permission === "granted") {
    const notification = new Notification(title, options);
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } else {
    console.log("Permission to send notifications is not granted");
  }
};

//return 16:00-16:30 PM to 04:00-04:30
export const convertTo12HourFormatTimeRange = (timeRange: string): string => {
  const [startTime, endTime] = timeRange.split("-");
  const startParts = startTime.trim().split(":");
  const endParts = endTime.trim().split(":");

  let startHour = parseInt(startParts[0], 10);
  let endHour = parseInt(endParts[0], 10);
  if (startHour >= 12) {
    startHour -= 12;
  }
  if (endHour >= 12) {
    endHour -= 12;
  }
  if (startHour === 0) {
    startHour = 12;
  }
  if (endHour === 0) {
    endHour = 12;
  }

  const formattedStartTime = `${startHour.toString().padStart(2, "0")}:${
    startParts[1]
  }`;
  const formattedEndTime = `${endHour.toString().padStart(2, "0")}:${
    endParts[1]
  }`;

  return `${formattedStartTime}-${formattedEndTime}`;
};

//return 18:00-18:30 to 18-18:30
export const removeTrailZerosInTime = (time: string) => {
  const timeArray = time.split("-");

  const firstTime = timeArray[0];
  const firstTimeHour = firstTime.split(":")[0];
  const firstTimeMinute = firstTime.split(":")[1];

  const secondTime = timeArray[1];
  const secondTimeHour = secondTime.split(":")[0];
  const secondTimeMinute = secondTime.split(":")[1];

  if (firstTimeMinute === "00") {
    return `${firstTimeHour}-${secondTime}`;
  } else if (secondTimeMinute === "00") {
    return `${firstTime}-${secondTimeHour}`;
  } else {
    return `${firstTime}-${secondTime}`;
  }
};

export const formatDateToDifferenceText = (dateString: string): string => {
  const now = new Date();
  const today = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    0,
    0,
    0,
    0
  );

  // Parse the YYYY-MM-DD format into a Date object
  const [year, month, day] = dateString
    .split("-")
    .map((num) => parseInt(num, 10));
  const givenDate = new Date(year, month - 1, day); // month is 0-indexed in JS Date

  // Calculate the difference in days between today and the given date
  const timeDifference = givenDate.getTime() - today.getTime();
  const differenceInDays = Math.ceil(timeDifference / (1000 * 3600 * 24));

  let formattedDate = "";
  let diff = "";

  if (differenceInDays === 0) {
    diff = "Today";
  } else if (differenceInDays === 1) {
    diff = "Tomorrow";
  }
  // Get the day of the week for the given date
  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const dayOfWeek = days[givenDate.getDay()];

  // Get the month name for the given date
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const monthName = months[givenDate.getMonth()];

  // Get the day of the month for the given date
  const dayOfMonth = givenDate.getDate();

  // Add appropriate suffix to the day of the month
  let suffix = "th";
  if (dayOfMonth === 1 || dayOfMonth === 21 || dayOfMonth === 31) {
    suffix = "st";
  } else if (dayOfMonth === 2 || dayOfMonth === 22) {
    suffix = "nd";
  } else if (dayOfMonth === 3 || dayOfMonth === 23) {
    suffix = "rd";
  }

  formattedDate = diff
    ? `${diff} ${dayOfWeek}, ${monthName} ${dayOfMonth}${suffix}`
    : `${dayOfWeek}, ${monthName} ${dayOfMonth}${suffix}`;

  return formattedDate;
};

export const convertSecondsSlotsToTime = (
  startSlotTimeSeconds: number,
  endSlotTimeSeconds: number
): string => {
  // Function to convert a number to a two-digit string (e.g., 7 -> "07")
  const twoDigitString = (num: number): string =>
    num.toString().padStart(2, "0");

  // Convert seconds to hours and minutes for start time
  const startHour = Math.floor(startSlotTimeSeconds / 3600);
  const startMinute = Math.floor((startSlotTimeSeconds % 3600) / 60);

  // Convert seconds to hours and minutes for end time
  const endHour = Math.floor(endSlotTimeSeconds / 3600);
  const endMinute = Math.floor((endSlotTimeSeconds % 3600) / 60);

  // Determine AM/PM suffix
  const startSuffix = startHour >= 12 ? "PM" : "AM";
  const endSuffix = endHour >= 12 ? "PM" : "AM";

  // Convert hours to 12-hour format
  const formattedStartHour = startHour % 12 === 0 ? 12 : startHour % 12;
  const formattedEndHour = endHour % 12 === 0 ? 12 : endHour % 12;

  // Construct formatted time strings
  const startTime = `${twoDigitString(formattedStartHour)}:${twoDigitString(
    startMinute
  )} ${startSuffix}`;
  const endTime = `${twoDigitString(formattedEndHour)}:${twoDigitString(
    endMinute
  )} ${endSuffix}`;

  // Return combined time range string
  return `${startTime} - ${endTime}`;
};

export const removeDuplicates = <T>(
  arr: T[],
  keyExtractor: (item: T) => string | number
): T[] => {
  const uniqueKeys: Set<string | number> = new Set();
  return arr.filter((obj) => {
    const key = keyExtractor(obj);
    if (uniqueKeys.has(key)) {
      return false;
    } else {
      uniqueKeys.add(key);
      return true;
    }
  });
};

export const getCirclePlayerDescription = (id: number) => {
  if (id === 1) {
    return "Players I've played with at least\n3 times";
  } else if (id === 2) {
    return "Players, I've played with in the past";
  } else if (id === 3) {
    return "Players that have NOT played with,\nbut played with my Inner and 1st circle";
  } else if (id === 4) {
    return "All other players";
  }
};

export const getInitials = (name: string) => {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

export const getRandomCoordinates = (
  innerRadius: number,
  outerRadius: number,
  height: number,
  coordinatesList: { x: number; y: number }[],
  imageSize: number
) => {
  // Random angle in radians
  const angle = Math.random() * 2 * Math.PI;

  // Random radius between innerRadius and outerRadius
  const radius = innerRadius + Math.random() * (outerRadius - innerRadius);

  // Convert polar coordinates to Cartesian coordinates
  let x = height / 2 + radius * Math.cos(angle);
  let y = height / 2 + radius * Math.sin(angle);

  for (let i = 0; i < coordinatesList.length; i++) {
    const x1 = coordinatesList[i].x;
    const y1 = coordinatesList[i].y;
    if (
      (x > x1 && x < x1 + imageSize && y > y1 && y < y1 + imageSize) ||
      (x + imageSize > x1 &&
        x + imageSize < x1 + imageSize &&
        y > y1 &&
        y < y1 + imageSize) ||
      (x + imageSize > x1 &&
        x + imageSize < x1 + imageSize &&
        y + imageSize > y1 &&
        y + imageSize < y1 + imageSize) ||
      (x > x1 &&
        x < x1 + imageSize &&
        y + imageSize > y1 &&
        y + imageSize < y1 + imageSize)
    ) {
      const { x: h, y: k } = getRandomCoordinates(
        innerRadius,
        outerRadius,
        height,
        coordinatesList,
        imageSize
      );
      x = h;
      y = k;
      break;
    }
  }
  return { x, y };
};

export const formatAmount = (num: number): string => {
  return num.toFixed(2); // Always formats to two decimal places
};
export const formatDateTime = (value: string) => {
  // Convert to Date obj
  const currentDate = new Date(value);

  // Get individual components of the date
  const day = String(currentDate.getDate()).padStart(2, "0");
  const month = String(currentDate.getMonth() + 1).padStart(2, "0");
  const year = currentDate.getFullYear();

  // Get individual components of the time
  let hours = currentDate.getHours();
  const minutes = String(currentDate.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12; // Convert 0 to 12 for 12-hour clock

  // Format the date and time
  const formattedDateTime = `${day}/${month}/${year} - ${hours}:${minutes} ${ampm}`;

  return formattedDateTime;
};
export const formatDate = (value: string) => {
  // Convert to Date obj
  const currentDate = new Date(value);

  // Get individual components of the date
  const day = String(currentDate.getDate()).padStart(2, "0");
  const month = String(currentDate.getMonth() + 1).padStart(2, "0");
  const year = currentDate.getFullYear();

  // Format the date and time
  const formattedDate = `${month}-${day}-${year}`;

  return formattedDate;
};

export const formatDateToCardDate = (dateString: string) => {
  const dateParts = dateString.split("-");
  const year = parseInt(dateParts[2], 10);
  const month = parseInt(dateParts[1], 10);

  // Format month with leading zero if needed
  const formattedMonth = month < 10 ? `0${month}` : `${month}`;

  // Extract last two digits of the year
  const formattedYear = year.toString().slice(2);

  // Concatenate formatted month and year
  const formattedDate = `${formattedMonth}/${formattedYear}`;

  return formattedDate;
};

export const convertToHours = (
  time: number,
  unit: "minutes" | "seconds" = "seconds"
): string => {
  let minutes = time;
  if (unit === "seconds") {
    // Convert seconds to minutes
    minutes = Math.floor(time / 60);
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const formattedHours =
    hours +
    (remainingMinutes > 0
      ? "." + ((remainingMinutes / 60) * 100).toFixed(0)
      : "");

  if (hours > 0 && remainingMinutes > 0) {
    return `${formattedHours} hours`;
  } else if (hours === 0 && remainingMinutes > 0) {
    return `${remainingMinutes} min`;
  } else {
    return `${hours} hours`;
  }
};

// iOS platform utility functions
export const getIOSAdjustedStyle = (baseStyle: any, iosStyle: any) => {
  return isIOS ? { ...baseStyle, ...iosStyle } : baseStyle;
};

export const isIOSPlatform = () => isIOS;

export type CardType =
  | "electron"
  | "maestro"
  | "dankort"
  | "interpayment"
  | "unionpay"
  | "visa"
  | "mastercard"
  | "amex"
  | "diners"
  | "discover"
  | "jcb";

export const detectCardTypeFromBIN = (bin: string): CardType | undefined => {
  // Ensure we're working with first 6 digits
  const firstSix = bin.slice(0, 6);

  const binPatterns: Record<CardType, string[]> = {
    electron: ["4026", "417500", "4405", "4508", "4844", "4913", "4917"],
    maestro: [
      "5018",
      "5020",
      "5038",
      "5612",
      "5893",
      "6304",
      "6759",
      "6761",
      "6762",
      "6763",
      "0604",
      "6390",
    ],
    dankort: ["5019"],
    interpayment: ["636"],
    unionpay: ["62", "88"],
    visa: ["4"],
    mastercard: ["51", "52", "53", "54", "55"],
    amex: ["34", "37"],
    diners: ["300", "301", "302", "303", "304", "305", "36", "38", "39"],
    discover: ["6011", "644", "645", "646", "647", "648", "649", "65"],
    jcb: ["35"],
  };

  for (const [type, patterns] of Object.entries(binPatterns)) {
    if (patterns.some((pattern) => firstSix.startsWith(pattern))) {
      return type as CardType;
    }
  }

  return undefined;
};

export const detectCardType = (number: string): CardType | undefined => {
  const re: Record<CardType, RegExp> = {
    electron: /^(4026|417500|4405|4508|4844|4913|4917)\d+$/,
    maestro:
      /^(5018|5020|5038|5612|5893|6304|6759|6761|6762|6763|0604|6390)\d+$/,
    dankort: /^(5019)\d+$/,
    interpayment: /^(636)\d+$/,
    unionpay: /^(62|88)\d+$/,
    visa: /^4[0-9]{12}(?:[0-9]{3})?$/,
    mastercard: /^5[1-5][0-9]{14}$/,
    amex: /^3[47][0-9]{13}$/,
    diners: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/,
    discover: /^6(?:011|5[0-9]{2})[0-9]{12}$/,
    jcb: /^(?:2131|1800|35\d{3})\d{11}$/,
  };

  for (const key in re) {
    if (re[key as CardType].test(number)) {
      return key as CardType;
    }
  }

  return undefined;
};

export const getDateInYYYYMMDDFormat = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
